import React, { useState, ChangeEvent, useEffect } from "react";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { parsePhoneNumberWithError } from "libphonenumber-js";

import { DASHBOARD_MEMBERS } from "../../../utils/routes";
import { signUpFirebase } from "../../../auth/operations";
import { startPhoneVerification, checkPhoneVerification } from "../../../api/apiEndpoints";
import { checkDuplicatesCall, getAllBusinessCall } from "../../../api/apiOperations";
import { setUserData } from "../../../slices/appSlice";
import { trackPixelEvent } from "../../../utils/pixel";
import { useApi } from "../../../hooks";

import NavbarContainer from "../../../components/NavbarContainer";
import FooterSection from "../FooterSection";
import GymHopSection from "../GymHopSection";

import BunnyEars from "../../../assets/images/BunnyEars.png";
import abtribecaImg from "../../../assets/images/member/images/ABtribeca (1).webp";
import omegaImg from "../../../assets/images/member/images/OMEGA+Inside-31 (1) (2).jpg";
import ruleturfImg from "../../../assets/images/member/images/ruleturf (2).png";
import pilatesImg from "../../../assets/images/member/images/Mat Pilates Omega (1).jpg";

import {
  PageContainer,
  HeroSection,
  HeroInner,
  HeroLeft,
  HeroCollage,
  CollageImg,
  FormCard,
  FormTitle,
  FormSubtitle,
  FieldRow,
  FieldFull,
  Input,
  InputWrapper,
  EyeButton,
  SubmitButton,
  BunnyIcon,
  CardErrorMessage,
  VerifyLinks,
  TextButton,
  VerifySeparator,
  HeroRight,
  HeroHeading,
  ProcessBubblesContainer,
  ProcessBubble,
  ProcessBubbleNumber,
  ProcessBubbleText,
  GymWrapper,
  FooterBox,
} from "./styles";

const SignUp: React.FC = () => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState<"form" | "verification">("form");
  const [verificationCode, setVerificationCode] = useState("");
  const [gymsLoading, setGymsLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [showRepeatPassword, setShowRepeatPassword] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { request: getAllBusinessRequest } = useApi({ apiFunc: getAllBusinessCall });

  useEffect(() => {
    const fetchGyms = async () => {
      await getAllBusinessRequest();
      setGymsLoading(false);
    };
    fetchGyms();
  }, []);

  const isFormValid =
    !!firstName && !!lastName && !!email && !!phone && !!password && !!repeatPassword;

  const validateEmail = (val: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

  const validatePhone = (val: string) => val.replace(/[^\d]/g, "").length === 10;

  const formatPhoneNumber = (value: string): string => {
    const digits = value.replace(/[^\d]/g, "").slice(0, 10);
    if (digits.length < 4) return digits;
    if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
  };

  const handlePhoneChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPhone(formatPhoneNumber(e.target.value));
  };

  const formattedPhoneDisplay = (() => {
    const digits = phone.replace(/[^\d]/g, "");
    if (digits.length === 10) {
      return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return `+1 ${digits}`;
  })();

  const sendVerificationCode = async (): Promise<boolean> => {
    setError("");

    if (!validateEmail(email)) {
      setError("Please enter a valid email address");
      return false;
    }
    if (!validatePhone(phone)) {
      setError("Please enter a valid 10-digit phone number");
      return false;
    }
    if (password !== repeatPassword) {
      setError("Passwords do not match");
      return false;
    }

    try {
      const e164Phone = `+1${phone.replace(/[^\d]/g, "")}`;
      const { isPhoneUnique, isEmailUnique, phoneMessage, emailMessage, message } =
        await checkDuplicatesCall(e164Phone, email);

      if (!isPhoneUnique || !isEmailUnique) {
        setError(
          !isPhoneUnique ? phoneMessage : emailMessage || message || "Phone or email is already in use."
        );
        return false;
      }

      const result = await startPhoneVerification(e164Phone);
      if (result?.success) {
        setStep("verification");
        return true;
      } else {
        setError(result?.message || "Failed to send verification code");
        return false;
      }
    } catch {
      setError("Failed to send verification code. Please try again.");
      return false;
    }
  };

  const handleNext = async () => {
    setLoading(true);
    await sendVerificationCode();
    setLoading(false);
  };

  const resendCode = async () => {
    setError("");
    setLoading(true);
    await sendVerificationCode();
    setLoading(false);
  };

  const handleVerificationSubmit = async () => {
    setError("");
    setLoading(true);

    try {
      const result = await checkPhoneVerification(
        `+1${phone.replace(/[^\d]/g, "")}`,
        verificationCode
      );

      if (!result?.success) {
        setError(result?.message || "Invalid verification code.");
        setLoading(false);
        return;
      }

      const signUpResult = await signUpFirebase(email, password, false, false, {
        firstName,
        lastName,
        phone: parsePhoneNumberWithError(phone, "US").number,
      });

      if (signUpResult !== "success") {
        setError(signUpResult);
        setLoading(false);
        return;
      }

      dispatch(setUserData({ email, firstName, lastName, phone }));
      trackPixelEvent("CompleteRegistration");
      navigate(DASHBOARD_MEMBERS);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <NavbarContainer dashboardScreen={false} checkoutScreen={false} />

      <HeroSection>
        <HeroInner>
          {/* Left: collage + overlaid form card */}
          <HeroLeft>
            <HeroCollage>
              <CollageImg pos="tl" src={abtribecaImg} alt="Gym" />
              <CollageImg pos="tr" src={omegaImg} alt="Omega Wellness" />
              <CollageImg pos="bl" src={ruleturfImg} alt="Rule Turf" />
              <CollageImg pos="br" src={pilatesImg} alt="Mat Pilates" />
            </HeroCollage>

            <FormCard>
              {step === "form" ? (
                <>
                  <FormTitle>Become a member</FormTitle>
                  <FormSubtitle>Create a free account and start exploring today</FormSubtitle>

                  {error && <CardErrorMessage>{error}</CardErrorMessage>}

                  <FieldRow>
                    <Input
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                    />
                    <Input
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </FieldRow>
                  <FieldFull>
                    <Input
                      type="email"
                      placeholder="Email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </FieldFull>
                  <FieldFull>
                    <Input
                      type="tel"
                      placeholder="Phone"
                      value={phone}
                      onChange={handlePhoneChange}
                    />
                  </FieldFull>
                  <FieldFull>
                    <InputWrapper>
                      <Input
                        type={showPassword ? "text" : "password"}
                        placeholder="Password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ paddingRight: 28 }}
                      />
                      <EyeButton type="button" onClick={() => setShowPassword((v) => !v)}>
                        {/* @ts-ignore */}
                        {showPassword ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                      </EyeButton>
                    </InputWrapper>
                  </FieldFull>
                  <FieldFull>
                    <InputWrapper>
                      <Input
                        type={showRepeatPassword ? "text" : "password"}
                        placeholder="Confirm password"
                        value={repeatPassword}
                        onChange={(e) => setRepeatPassword(e.target.value)}
                        style={{ paddingRight: 28 }}
                      />
                      <EyeButton type="button" onClick={() => setShowRepeatPassword((v) => !v)}>
                        {/* @ts-ignore */}
                        {showRepeatPassword ? <FaEyeSlash size={13} /> : <FaEye size={13} />}
                      </EyeButton>
                    </InputWrapper>
                  </FieldFull>

                  <SubmitButton onClick={handleNext} disabled={!isFormValid || loading}>
                    <BunnyIcon src={BunnyEars} alt="" />
                    {loading ? "Sending code..." : "Create account →"}
                  </SubmitButton>
                </>
              ) : (
                <>
                  <FormTitle>Verify your number</FormTitle>
                  <FormSubtitle>We sent a 6-digit code to {formattedPhoneDisplay}</FormSubtitle>

                  {error && <CardErrorMessage>{error}</CardErrorMessage>}

                  <FieldFull>
                    <Input
                      placeholder="6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value)}
                      maxLength={6}
                    />
                  </FieldFull>

                  <SubmitButton
                    onClick={handleVerificationSubmit}
                    disabled={!verificationCode || loading}
                  >
                    {loading ? "Verifying..." : "Verify & Create Account"}
                  </SubmitButton>

                  <VerifyLinks>
                    <TextButton onClick={resendCode} disabled={loading}>
                      Resend code
                    </TextButton>
                    <VerifySeparator>·</VerifySeparator>
                    <TextButton onClick={() => { setStep("form"); setError(""); }}>
                      Change number
                    </TextButton>
                  </VerifyLinks>
                </>
              )}
            </FormCard>
          </HeroLeft>

          {/* Right: heading + process bubbles */}
          <HeroRight>
            <HeroHeading>
              Start with a month, stay where you belong
            </HeroHeading>
            <ProcessBubblesContainer>
              <ProcessBubble>
                <ProcessBubbleNumber>1</ProcessBubbleNumber>
                <ProcessBubbleText>Sign up and explore locations near you</ProcessBubbleText>
              </ProcessBubble>
              <ProcessBubble>
                <ProcessBubbleNumber>2</ProcessBubbleNumber>
                <ProcessBubbleText>Choose one and get unlimited access for the month</ProcessBubbleText>
              </ProcessBubble>
              <ProcessBubble>
                <ProcessBubbleNumber>3</ProcessBubbleNumber>
                <ProcessBubbleText>Stay where you belong, or keep exploring</ProcessBubbleText>
              </ProcessBubble>
            </ProcessBubblesContainer>
          </HeroRight>
        </HeroInner>
      </HeroSection>

      <GymWrapper>
        {!gymsLoading && <GymHopSection showSavings={true} buttonAction="scrolltop" buttonText="Create your free account" />}
      </GymWrapper>

      <FooterBox>
        <FooterSection />
      </FooterBox>
    </PageContainer>
  );
};

export default SignUp;
